import React from 'react';
import {
  Button,
  Checkbox,
  IconButton,
  Tooltip,
  Autocomplete,
  TextField,
  Popper,
  CircularProgress,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import TableChartIcon from '@mui/icons-material/TableChart';
import CompareIcon from '@mui/icons-material/Compare';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChatIcon from '@mui/icons-material/Chat';
import { styled } from "@mui/material/styles";
import Constants from '../../../assests/Constants/constants.js';

// Sleek styled button component
const SleekButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  borderRadius: '8px',
  
  fontSize: '12px',
  fontWeight: '600',
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  border: 'none',
  color: '#ffffff',
  minWidth: 'auto',
  height: '36px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
  },
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
  },
  '& .MuiButton-startIcon': {
    marginRight: '6px',
    marginLeft: '0px',
    '& svg': {
      fontSize: '16px',
    }
  }
}));

// Sleek icon button component
const SleekIconButton = styled(IconButton)(({ theme }) => ({
  background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
  borderRadius: '8px',
  width: '36px',
  height: '36px',
  boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
  color: '#ffffff',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #45a049 0%, #3d8b40 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)',
  },
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
  },
  '& svg': {
    fontSize: '18px',
  }
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 'var(--Spacing-md, 8px)',
    background: 'var(--white, #FFF)',
    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.32)',
  },
  '& .MuiAutocomplete-listbox': {
    padding: theme.spacing(1),
  },
  '& .MuiAutocomplete-option': {
    borderRadius: 'var(--Spacing-sm, 4px)',
    '&[aria-selected="true"], &.Mui-focused, &:hover': {
      background: 'var(--orange, #F9AA2A) !important',
    },
  },
}));

const ReportToolbar = ({
  direction,
  showAccountCardsWithZeros,
  showCards,
  showAccountCodeId,
  showPercentageColumn,
  showPeriodComparison,
  onDownloadPdf,
  onDownloadExcel,
  onPrint,
  onSendToChat,
  onShowZeroCardsToggle,
  onShowCardsToggle,
  onShowAccountCodeIdToggle,
  onShowPercentageColumnToggle,
  onOpenPeriodSelection,
  onCloseComparison,
  onOpenMonthlyReport,
  t,
  userTypeContext,
  byProjectsFilterActive,
  onByProjectsFilterToggle,
  managesProject = false,
  segmentationProjects,
  loadingSegmentations,
  selectedFromSegmentation,
  selectedToSegmentation,
  onFromSegmentationChange,
  onToSegmentationChange,
  hideLiveDataTools = false,
}) => {
  const showProjectsFilter = managesProject && !hideLiveDataTools;
  const projectAutocompleteOptions = (segmentationProjects || []).map((p) => ({
    id: String(p.id),
    code: p.code,
    name: p.name,
    label: p.label,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'center' }} className={`${direction}`}>
      <div
        className="pdf-view-upper"
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: '15px',
          flexWrap: 'wrap',
          gap: '6px',
          padding: '6px',
        }}
      >
        <SleekButton
          onClick={onDownloadPdf}
          startIcon={<DownloadIcon />}
        >
          {t('DownloadPDFFile')}
        </SleekButton>

        <SleekButton
          onClick={onDownloadExcel}
          startIcon={<TableChartIcon />}
        >
          {t('DownloadExcelFile')}
        </SleekButton>

        <SleekButton
          onClick={onPrint}
          startIcon={<PrintIcon />}
        >
          {t('PrintReport')}
        </SleekButton>

        {
          userTypeContext?.toString() !== Constants.UserType.Client &&

          <SleekButton
            onClick={onSendToChat}
            startIcon={<ChatIcon />}
          >
            {t('SendToChat')}
          </SleekButton>
        }
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          backgroundColor: '#f8f9ff',
          borderRadius: '8px',
          border: '1px solid #e3e8ff',
          height: '36px',
        }}>
          <Checkbox
            checked={showAccountCardsWithZeros}
            onChange={(event) => onShowZeroCardsToggle(event.target.checked)}
            name="ShowResetCards"
            color="primary"
            sx={{ padding: '2px' }}
            size="small"
          />
          <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
            {t('ShowResetCards')}
          </span>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          backgroundColor: '#f8f9ff',
          borderRadius: '8px',
          border: '1px solid #e3e8ff',
          height: '36px',
        }}>
          <Checkbox
            checked={showCards}
            onChange={(event) => onShowCardsToggle(event.target.checked)}
            name="ShowCards"
            color="primary"
            sx={{ padding: '2px' }}
            size="small"
          />
          <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
            {t('ShowCards')}
          </span>
        </div>

        {showCards && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          backgroundColor: '#f8f9ff',
          borderRadius: '8px',
          border: '1px solid #e3e8ff',
          height: '36px',
        }}>
          <Checkbox
            checked={!!showAccountCodeId}
            onChange={(event) => onShowAccountCodeIdToggle?.(event.target.checked)}
            name="ShowAccountCodeId"
            color="primary"
            sx={{ padding: '2px' }}
            size="small"
          />
          <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
            {t('ShowAccountCodeId')}
          </span>
        </div>
        )}

        {showProjectsFilter && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          backgroundColor: '#f8f9ff',
          borderRadius: '8px',
          border: '1px solid #e3e8ff',
          height: '36px',
        }}>
          <Checkbox
            checked={!!byProjectsFilterActive}
            onChange={(event) => onByProjectsFilterToggle?.(event.target.checked)}
            name="ByProjects"
            color="primary"
            sx={{ padding: '2px' }}
            size="small"
          />
          <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
            {t('ByProjects')}
          </span>
        </div>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 12px',
          backgroundColor: '#f8f9ff',
          borderRadius: '8px',
          border: '1px solid #e3e8ff',
          height: '36px',
        }}>
          <Checkbox
            checked={showPercentageColumn}
            onChange={(event) => onShowPercentageColumnToggle(event.target.checked)}
            name="ShowPercentageColumn"
            color="primary"
            sx={{ padding: '2px' }}
            size="small"
          />
          <span style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}>
            {t('ShowPercentageColumn')}
          </span>
        </div>

        {!hideLiveDataTools && (
        <SleekButton
          onClick={onOpenMonthlyReport}
          startIcon={<CalendarMonthIcon />}
        >
          {t('MonthlyReport')}
        </SleekButton>
        )}

        {hideLiveDataTools ? null : !showPeriodComparison ? (
          <SleekButton
            onClick={onOpenPeriodSelection}
            startIcon={<CompareIcon />}
          >
            {t('ComparePeriods')}
          </SleekButton>
        ) : (
          <SleekButton
            onClick={onCloseComparison}
            sx={{
              background: 'linear-gradient(135deg, #FF6B35 0%, #E55A2B 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #E55A2B 0%, #D44A1A 100%)',
              }
            }}
          >
            {t('CloseComparison')}
          </SleekButton>
        )}

      </div>

      {showProjectsFilter && byProjectsFilterActive && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '0',
            marginBottom: '12px',
            paddingBottom: '4px',
          }}
        >
          {loadingSegmentations && <CircularProgress size={22} />}
          <Autocomplete
            size="small"
            loading={loadingSegmentations}
            options={projectAutocompleteOptions}
            getOptionLabel={(opt) => opt?.label ?? ''}
            isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
            value={selectedFromSegmentation}
            onChange={onFromSegmentationChange}
            PopperComponent={StyledPopper}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('FromProject')}
                size="small"
                dir={direction}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    background: '#F5F5F6',
                    '&:focus-within': {
                      outline: '2px solid #304FFF',
                    },
                  },
                }}
              />
            )}
            clearOnBlur={false}
            dir={direction}
            sx={{ minWidth: 260, maxWidth: 360 }}
          />
          <Autocomplete
            size="small"
            loading={loadingSegmentations}
            options={projectAutocompleteOptions}
            getOptionLabel={(opt) => opt?.label ?? ''}
            isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
            value={selectedToSegmentation}
            onChange={onToSegmentationChange}
            PopperComponent={StyledPopper}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('ToProject')}
                size="small"
                dir={direction}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    background: '#F5F5F6',
                    '&:focus-within': {
                      outline: '2px solid #304FFF',
                    },
                  },
                }}
              />
            )}
            clearOnBlur={false}
            dir={direction}
            sx={{ minWidth: 260, maxWidth: 360 }}
          />
        </div>
      )}
    </div>
  );
};

export default ReportToolbar;
